import { getDbSafe } from '../services/firebaseConfig';
import { doc, runTransaction } from 'firebase/firestore';
import { SubscriptionTier, ProfileBackgroundTheme } from '../types';

export const SubscriptionService = {
    getSubscriptionEntitlements(tier: SubscriptionTier) {
        return {
            streakMultiplier: tier === 'elite' ? 3 : (tier === 'yapper' ? 2 : 1),
            anonymousDailyBonus: tier !== 'none' ? 2 : 0, // Both paid tiers get +2
            canCustomizeBackground: tier === 'elite',
            prioritySupport: tier !== 'none',
            canCreateForum: true, // Everyone can, but checking limits/credits differs
            canCreateTribe: true,
        };
    },

    /**
     * Calculate credits to grant based on CURRENT ownership count.
     * FORUM credits (cap 3 owned): 0->3, 1->2, 2->1, 3->0
     * TRIBE credits (cap 2 owned): 0->2, 1->1, 2->0
     */
    calculateRefillCredits(ownedForumsCount: number, ownedTribesCount: number) {
        const forumCap = 3;
        const tribeCap = 2;

        const forumCredits = Math.max(0, forumCap - ownedForumsCount);
        const tribeCredits = Math.max(0, tribeCap - ownedTribesCount);

        return { forumCredits, tribeCredits };
    },

    /**
     * Grant credits logic (usually called via Cloud Function or webhook upon successful payment, 
     * but implementing client-side/service wrapper for now as requested).
     * WARNING: In production, this must be server-side to prevent tampering.
     */
    async grantCreationCreditsOnPurchaseOrRenew(userId: string, tier: SubscriptionTier) {
        if (tier === 'none') return; // Basic tier doesn't get refills usually? Or does it? 
        // Spec says: "When subscription is purchased or renews, grant credits based on what user currently owns"
        // Implied for Paid tiers only? "Subscription grants creation credits..." -> Yes.

        const db = await getDbSafe();
        const userRef = doc(db, 'users', userId);
        await runTransaction(db, async (transaction) => {
            const userDoc = await transaction.get(userRef);
            if (!userDoc.exists()) throw new Error("User not found");
            const data = userDoc.data();

            const ownedForums = data.ownedForumIds || [];
            const ownedTribes = data.ownedTribeIds || [];

            const { forumCredits, tribeCredits } = this.calculateRefillCredits(ownedForums.length, ownedTribes.length);

            transaction.update(userRef, {
                forumCreationCredits: forumCredits,
                tribeCreationCredits: tribeCredits,
                subscriptionTier: tier,
                subscriptionActive: true,
                // Update theme if downgrading/expiring (handled in a separate check usually, but here we set active)
            });
        });
    }
};
